import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Smartphone, Download, Database, Lock, Folder, Share2, Monitor } from "lucide-react";

export default function StoreitDetail() {
  const navigate = useNavigate();

  const playStoreUrl = "https://play.google.com/store/apps/details?id=com.store.it.app";
  const webAppUrl = "https://storeit.hickter.com";

  const features = [
    {
      title: "256-Bit Encrypted Cloud Vault",
      desc: "Bank-grade SSL end-to-end encryption for photos, videos, documents, and private device backups.",
      icon: <Lock size={20} className="text-blue-600 dark:text-cyan-400" />
    },
    {
      title: "Smart File Organization & Tagging",
      desc: "Intelligent automatic folder tagging, file type categorization, and instant multi-keyword search.",
      icon: <Folder size={20} className="text-indigo-600 dark:text-indigo-400" />
    },
    {
      title: "Sub-Second Cross-Device Sync",
      desc: "Real-time background sync across iOS, Android smartphones, desktop web browsers, and cloud backups.",
      icon: <Database size={20} className="text-emerald-600 dark:text-emerald-400" />
    },
    {
      title: "Granular Access Control & Sharing",
      desc: "Generate password-protected download links, set link expiration timers, and grant view-only file permissions.",
      icon: <Share2 size={20} className="text-purple-600 dark:text-purple-400" />
    }
  ];

  const techStack = ["React.js", "Laravel", "PHP", "MySQL", "TailwindCSS", "Cloud S3 API"];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#030712] text-slate-900 dark:text-slate-100 font-sans pb-32 pt-6 px-4 sm:px-6 lg:px-8 transition-colors duration-300">
      <div className="max-w-5xl mx-auto space-y-8">
        
        {/* TOP BACK BUTTON */}
        <div>
          <button 
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-cyan-400 font-bold text-xs transition-all shadow-sm cursor-pointer group"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            <span>Back to Products</span>
          </button>
        </div>

        {/* HERO CARD */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10 shadow-xl space-y-8 relative overflow-hidden">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 p-3 shrink-0 shadow-md flex items-center justify-center">
                <img 
                  src="https://ik.imagekit.io/filennky/Products%20Hickter/mylogo.png" 
                  alt="Storeit Logo" 
                  className="w-full h-full object-contain rounded-xl"
                />
              </div>
              <div className="space-y-1">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-bold">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  Google PlayStore Available
                </div>
                <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                  Storeit – Keep It With You
                </h1>
                <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm font-medium">
                  Personal & Enterprise Secure Cloud Storage Solution
                </p>
              </div>
            </div>

            {/* ACTION LINKS */}
            <div className="flex items-center gap-3 w-full md:w-auto">
              <a
                href={playStoreUrl}
                target="_blank"
                rel="noreferrer"
                className="btn-primary-blue flex-1 md:flex-initial py-3 px-5 text-xs font-bold rounded-xl flex items-center justify-center gap-2 !text-white shadow-lg shadow-blue-500/20"
              >
                <Smartphone size={16} className="!text-white" />
                <span className="!text-white">PlayStore</span>
                <ExternalLink size={13} className="!text-white" />
              </a>

              <a
                href={webAppUrl}
                target="_blank"
                rel="noreferrer"
                className="flex-1 md:flex-initial py-3 px-5 text-xs font-extrabold rounded-xl bg-black dark:bg-black text-white !text-white border border-slate-800 flex items-center justify-center gap-2 shadow-md hover:bg-slate-900 transition-colors"
              >
                <Monitor size={16} className="!text-white" />
                <span className="!text-white font-extrabold">Web Login</span>
              </a>
            </div>
          </div>

          {/* OVERVIEW SECTION */}
          <div className="space-y-3 pt-4 border-t border-slate-100 dark:border-slate-800">
            <h3 className="text-base font-extrabold text-slate-900 dark:text-white">Product Overview</h3>
            <p className="text-slate-600 dark:text-slate-300 text-xs sm:text-sm leading-relaxed font-medium">
              Storeit – Keep It With You is a high-speed personal and enterprise cloud storage application. Designed to provide uninterrupted file access, Storeit allows users to safely backup confidential files, photos, media archives, and corporate documentation with encrypted access control.
            </p>
          </div>

          {/* TECH STACK BADGES */}
          <div className="space-y-3 pt-2">
            <h4 className="text-xs font-extrabold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Built With Modern Stack:
            </h4>
            <div className="flex flex-wrap gap-2">
              {techStack.map((tech, idx) => (
                <span key={idx} className="px-3 py-1 rounded-xl bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 text-blue-700 dark:text-cyan-400 text-xs font-bold">
                  #{tech}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* CORE FEATURES GRID */}
        <div className="space-y-4">
          <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">Core Capabilities & Modules</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {features.map((feat, idx) => (
              <div 
                key={idx}
                className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-md space-y-2"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800">
                    {feat.icon}
                  </div>
                  <h4 className="text-sm font-extrabold text-slate-900 dark:text-white">{feat.title}</h4>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-400 font-medium leading-relaxed">
                  {feat.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* FIXED BOTTOM DOWNLOAD & ACCESS BAR */}
      <div className="fixed bottom-0 left-0 right-0 z-[110] bg-white/95 dark:bg-[#030712]/95 backdrop-blur-xl border-t border-slate-200 dark:border-slate-800 p-3 sm:p-4 shadow-2xl">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 sm:gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 p-1.5 shrink-0 hidden sm:flex items-center justify-center">
              <img 
                src="https://ik.imagekit.io/filennky/Products%20Hickter/mylogo.png" 
                alt="Storeit Logo" 
                className="w-full h-full object-contain rounded-lg"
              />
            </div>
            <div className="text-left">
              <h4 className="text-xs sm:text-sm font-extrabold text-slate-900 dark:text-white truncate">Storeit – Keep It With You</h4>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium hidden sm:block">Google PlayStore Cloud Storage Vault</p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 w-full sm:w-auto shrink-0">
            <a
              href={playStoreUrl}
              target="_blank"
              rel="noreferrer"
              className="flex-1 sm:flex-initial py-2.5 px-5 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 hover:from-blue-500 hover:to-indigo-600 text-white font-extrabold text-xs flex items-center justify-center gap-2 transition-all shadow-md shadow-blue-500/25 !text-white"
            >
              <Download size={15} className="!text-white" />
              <span className="!text-white font-black">Get App</span>
            </a>

            <a
              href={webAppUrl}
              target="_blank"
              rel="noreferrer"
              className="flex-1 sm:flex-initial py-2.5 px-4 rounded-xl bg-black dark:bg-black text-white !text-white border border-slate-800 text-xs font-extrabold flex items-center justify-center gap-2 shadow-sm hover:bg-slate-900 transition-colors"
            >
              <Monitor size={15} className="!text-white" />
              <span className="!text-white font-extrabold">Web Login</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
